import { useTranslation } from 'react-i18next';
import {
  formatDate,
  formatRelativeDate,
  formatDateDistance,
  formatNumber,
  formatCurrency,
  formatPercent,
  formatCompactNumber,
  formatTime,
} from '@/lib/locale-formatting';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Hook for locale-aware formatting.
 *
 * Currency is sourced from the farmer's countries row (currency_code +
 * currency_symbol), carried on the AuthContext user — no hardcoded maps and no
 * BDT/USD fallback. Number grouping/decimals come from Intl via the current
 * language locale. If the country row hasn't resolved, formatCurrency renders a
 * neutral number with no symbol (never a wrong currency).
 */
export const useLocaleFormat = () => {
  const { i18n } = useTranslation();
  const { user } = useAuth();

  const currentLanguage = i18n.language;
  const currencyCode = user?.currencyCode ?? null;
  const currencySymbol = user?.currencySymbol ?? null;

  return {
    /**
     * Format a date according to the current locale
     */
    formatDate: (date: Date | string | number, formatStr?: string) =>
      formatDate(date, formatStr, currentLanguage),

    /**
     * Format a date relative to now (e.g., "2 hours ago")
     */
    formatRelativeDate: (date: Date | string | number, baseDate?: Date) =>
      formatRelativeDate(date, baseDate, currentLanguage),

    /**
     * Format distance between dates (e.g., "2 hours")
     */
    formatDateDistance: (date: Date | string | number, baseDate?: Date, addSuffix?: boolean) =>
      formatDateDistance(date, baseDate, currentLanguage, addSuffix),

    /**
     * Format a number according to the current locale
     */
    formatNumber: (value: number, options?: Intl.NumberFormatOptions) =>
      formatNumber(value, currentLanguage, options),

    /**
     * Format currency for the farmer's country (countries.currency_code +
     * currency_symbol). Optional overrides accept a specific ISO code + symbol.
     */
    formatCurrency: (value: number, currencyCodeOverride?: string, currencySymbolOverride?: string) =>
      formatCurrency(
        value,
        currentLanguage,
        currencyCodeOverride ?? currencyCode,
        currencySymbolOverride ?? currencySymbol,
      ),

    /**
     * Format percentage according to the current locale
     */
    formatPercent: (value: number, minDigits?: number, maxDigits?: number) =>
      formatPercent(value, currentLanguage, minDigits, maxDigits),

    /**
     * Format compact number (e.g., 1000 -> 1K)
     */
    formatCompactNumber: (value: number) =>
      formatCompactNumber(value, currentLanguage),

    /**
     * Format a time value
     */
    formatTime: (date: Date | string | number, includeSeconds?: boolean) =>
      formatTime(date, currentLanguage, includeSeconds),

    /**
     * Current language code
     */
    locale: currentLanguage,

    /**
     * Farmer's currency, single-sourced from the countries row
     */
    currencyCode,
    currencySymbol,
  };
};
